import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import "./lexical.css";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isDecoratorNode,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";

const isJson = (str) => {
  if (typeof str !== "string") return false;
  const trimmed = str.trim();
  return trimmed.startsWith("{") && trimmed.endsWith("}");
};

const isHtml = (str) => {
  if (typeof str !== "string") return false;
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, "text/html");
  return Array.from(doc.body.childNodes).some(
    (node) => node.nodeType === Node.ELEMENT_NODE
  );
};

export const TextEditor = ({ value, ref }) => {
  const initialConfig = {
    namespace: "MyEditor",
    theme: {
      code: "editor-code",
      heading: {
        h1: "editor-heading-h1",
        h2: "editor-heading-h2",
        h3: "editor-heading-h3",
        h4: "editor-heading-h4",
        h5: "editor-heading-h5",
      },
      image: "editor-image",
      link: "editor-link",
      list: {
        listitem: "editor-listitem",
        nested: {
          listitem: "editor-nested-listitem",
        },
        ol: "editor-list-ol",
        ul: "editor-list-ul",
      },
      paragraph: "editor-paragraph",
      placeholder: "editor-placeholder",
      quote: "editor-quote",
      text: {
        bold: "editor-text-bold",
        code: "editor-text-code",
        hashtag: "editor-text-hashtag",
        italic: "editor-text-italic",
        overflowed: "editor-text-overflowed",
        strikethrough: "editor-text-strikethrough",
        underline: "editor-text-underline",
        underlineStrikethrough: "editor-text-underlineStrikethrough",
      },
    },
    onError: console.error
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="editor-container">
        <ToolbarPlugin />

        <div className="editor-inner">
          <RichTextPlugin
            contentEditable={<ContentEditable className="editor-input" />}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <EditorActions ref={ref} initialValue={value} />
        </div>
      </div>
    </LexicalComposer>
  );
};

const EditorActions = ({ initialValue, ref }) => {
  const [editor] = useLexicalComposerContext();

  useImperativeHandle(ref, () => ({
    getHtml: () => {
      let html = "";

      editor.getEditorState().read(() => {
        const root = $getRoot();

        if (root.getTextContent().trim() === "") {
          html = "";
        } else {
          html = $generateHtmlFromNodes(editor, null);
        }
      });

      return html;
    }
  }));

  // Sync initialValue to editor state on mount
  useEffect(() => {
    const normalizedValue = initialValue || "";

    if (isJson(normalizedValue)) {
      try {
        const editorState = editor.parseEditorState(normalizedValue);
        editor.setEditorState(editorState);
      } catch (e) {
        console.error("Failed to parse Lexical JSON state", e);
      }
    } else {
      if (isHtml(normalizedValue)) {
        editor.update(() => {
          const parser = new DOMParser();
          const dom = parser.parseFromString(normalizedValue, "text/html");
          const nodes = $generateNodesFromDOM(editor, dom);
          const root = $getRoot();
          root.clear();

          let activeParagraph = null;
          nodes.forEach((node) => {
            if ($isElementNode(node) || $isDecoratorNode(node)) {
              root.append(node);
              activeParagraph = null;
            } else if ($isTextNode(node)) {
              const textContent = node.getTextContent();
              if (textContent.trim() === "" && !activeParagraph) {
                return;
              }
              if (!activeParagraph) {
                activeParagraph = $createParagraphNode();
                root.append(activeParagraph);
              }
              activeParagraph.append(node);
            }
          });
        });
      } else {
        // Plain text legacy string
        editor.update(() => {
          const root = $getRoot();
          root.clear();
          const lines = normalizedValue.split("\n");
          lines.forEach((line) => {
            const paragraph = $createParagraphNode();
            paragraph.append($createTextNode(line));
            root.append(paragraph);
          });
        });
      }
    }
  }, [editor, initialValue]);

  return null;
};

const Divider = () => {
  return <div className="divider" />;
};

const ToolbarPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(
          () => {
            $updateToolbar();
          },
          { editor },
        );
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, $updateToolbar]);

  const handleFormat = (command, format) => (e) => {
    e.preventDefault();

    editor.dispatchCommand(command, format);
  };

  return (
    <div className="toolbar" ref={toolbarRef}>
      <button
        onClick={handleFormat(FORMAT_TEXT_COMMAND, "bold")}
        className={"toolbar-item spaced " + (isBold ? "active" : "")}
        aria-label="Format Bold">
        <i className="format bold" />
      </button>
      <button
        onClick={handleFormat(FORMAT_TEXT_COMMAND, "italic")}
        className={"toolbar-item spaced " + (isItalic ? "active" : "")}
        aria-label="Format Italics">
        <i className="format italic" />
      </button>
      <button
        onClick={handleFormat(FORMAT_TEXT_COMMAND, "underline")}
        className={"toolbar-item spaced " + (isUnderline ? "active" : "")}
        aria-label="Format Underline">
        <i className="format underline" />
      </button>
      <button
        onClick={handleFormat(FORMAT_TEXT_COMMAND, "strikethrough")}
        className={"toolbar-item spaced " + (isStrikethrough ? "active" : "")}
        aria-label="Format Strikethrough">
        <i className="format strikethrough" />
      </button>
      <Divider />
      <button
        onClick={handleFormat(FORMAT_ELEMENT_COMMAND, "left")}
        className="toolbar-item spaced"
        aria-label="Left Align">
        <i className="format left-align" />
      </button>
      <button
        onClick={handleFormat(FORMAT_ELEMENT_COMMAND, "center")}
        className="toolbar-item spaced"
        aria-label="Center Align">
        <i className="format center-align" />
      </button>
      <button
        onClick={handleFormat(FORMAT_ELEMENT_COMMAND, "right")}
        className="toolbar-item spaced"
        aria-label="Right Align">
        <i className="format right-align" />
      </button>
      <button
        onClick={handleFormat(FORMAT_ELEMENT_COMMAND, "justify")}
        className="toolbar-item"
        aria-label="Justify Align">
        <i className="format justify-align" />
      </button>
      {" "}
    </div>
  );
};
